import React, { useState, useEffect, useRef } from "react";
import Layout from "../components/Layout";
import {
  Calculator, Target, Calendar, Loader2,
  Wallet, AlertCircle, Percent,
  TrendingUp, ChevronLeft, ChevronRight,
  Gauge, Trophy, ArrowRight, DollarSign, BarChart3, Clock, Flag, Gift,
} from "lucide-react";
import axiosInstance from "../configApi/axiosConfig";
import { APIURL } from "../configApi/apiConfig";

const SLIDER_MAX = 200;
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

function fmtMoney(n) {
  return `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function periodoActual() {
  const now = new Date();
  return `${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;
}

function nombrePeriodo(periodoStr) {
  const [m, y] = periodoStr.split('/');
  const nombre = MESES[parseInt(m, 10) - 1];
  return nombre ? `${nombre} ${y}` : periodoStr;
}

function shiftPeriodo(periodoStr, delta) {
  const [mStr, yStr] = periodoStr.split('/');
  let m = parseInt(mStr, 10), y = parseInt(yStr, 10);
  if (!m || !y) return periodoStr;
  m += delta;
  if (m < 1) { m = 12; y -= 1; }
  if (m > 12) { m = 1; y += 1; }
  return `${String(m).padStart(2, '0')}/${y}`;
}

// Clasifica el % de avance real en 3 tramos (rojo/ámbar/verde), el mismo
// código de colores se reutiliza en el badge, el anillo y la barra de ritmo
// para que el vendedor lea su situación de un vistazo sin depender solo del número.
function tierAvance(pct) {
  if (pct >= 100) return { color: '#00B517', bg: 'bg-success/10', text: 'text-success', label: 'Meta superada' };
  if (pct >= 60) return { color: '#f59e0b', bg: 'bg-amber-50', text: 'text-amber-600', label: 'En buen camino' };
  return { color: '#eb2a1b', bg: 'bg-naranja/10', text: 'text-naranja', label: 'Necesitas acelerar' };
}

// Anillo de progreso vía stroke-dasharray — reemplaza al genérico "barra +
// número" para que el avance real del mes se lea como una sola forma, sin
// tener que comparar dos cifras mentalmente.
function RadialProgress({ pct, size = 96, stroke = 9, color }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = c * (Math.min(pct, 100) / 100);
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="radial-ring -rotate-90"
      role="img"
      aria-label={`${Math.round(pct)} por ciento de tu meta mensual alcanzado`}
    >
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={stroke} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c - dash}
        style={{ transition: 'stroke-dashoffset 500ms ease, stroke 300ms ease' }}
      />
      <style>{`
        @media (prefers-reduced-motion: reduce) {
          .radial-ring circle { transition: none !important; }
        }
      `}</style>
    </svg>
  );
}

// Slider horizontal con relleno progresivo ("barra que sube") — reemplaza
// el patrón genérico de chips +/-. El track se rellena con el color que
// corresponde al tramo (en progreso / meta cumplida), y una marca fija en
// el 100% ubica visualmente dónde está "tu meta" dentro de la escala.
function SliderPorcentaje({ value, onChange }) {
  const pct = Math.min(100, (value / SLIDER_MAX) * 100);
  const metaMarkPct = (100 / SLIDER_MAX) * 100;
  const color = value >= 100 ? '#059669' : '#2d3689';

  return (
    <div className="w-full">
      <div className="flex items-end justify-between">
        <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-400">
          <Target size={11} /> Porcentaje a proyectar
        </span>
        <div className="flex items-baseline gap-0.5">
          <input
            type="number"
            value={value}
            onChange={e => onChange(Math.max(0, parseFloat(e.target.value) || 0))}
            className="w-12 bg-transparent text-right text-xl font-black tracking-tight focus:outline-none"
            style={{ color }}
          />
          <span className="text-base font-black" style={{ color }}>%</span>
        </div>
      </div>

      <div className="relative mt-1.5 pb-2.5">
        <input
          type="range"
          min={0}
          max={SLIDER_MAX}
          step={1}
          value={Math.min(value, SLIDER_MAX)}
          onChange={e => onChange(Number(e.target.value))}
          aria-label="Porcentaje de meta a proyectar"
          className="sim-slider w-full"
          style={{ '--sim-fill': `${pct}%`, '--sim-color': color }}
        />
        <div
          className="pointer-events-none absolute top-0 flex -translate-x-1/2 flex-col items-center"
          style={{ left: `${metaMarkPct}%` }}
        >
          <div className="h-3 w-0.5 bg-slate-300" />
          <span className="mt-0.5 text-[8px] font-bold uppercase tracking-wide text-slate-400">100%</span>
        </div>
      </div>

      <style>{`
        .sim-slider {
          -webkit-appearance: none;
          appearance: none;
          height: 8px;
          border-radius: 9999px;
          background: linear-gradient(to right, var(--sim-color) var(--sim-fill), #e2e8f0 var(--sim-fill));
          outline: none;
          transition: background 150ms ease;
        }
        .sim-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 9999px;
          background: #ffffff;
          border: 3px solid var(--sim-color);
          box-shadow: 0 2px 6px rgba(15, 23, 42, 0.25);
          cursor: pointer;
          transition: transform 150ms ease;
        }
        .sim-slider::-webkit-slider-thumb:hover { transform: scale(1.12); }
        .sim-slider::-moz-range-thumb {
          width: 18px;
          height: 18px;
          border-radius: 9999px;
          background: #ffffff;
          border: 3px solid var(--sim-color);
          box-shadow: 0 2px 6px rgba(15, 23, 42, 0.25);
          cursor: pointer;
        }
        .sim-slider::-moz-range-track { height: 8px; border-radius: 9999px; background: transparent; }
        .sim-slider:focus-visible::-webkit-slider-thumb { outline: 2px solid var(--sim-color); outline-offset: 2px; }
      `}</style>
    </div>
  );
}

export default function SimuladorMetas() {
  const [periodo, setPeriodo] = useState(periodoActual);
  const [porcentaje, setPorcentaje] = useState(100);
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const debounceRef = useRef(null);
  const primerCargaRef = useRef(true);

  const esPeriodoFuturo = (per) => {
    const [monthStr, yearStr] = per.split('/');
    if (!monthStr || !yearStr) return false;
    const inputMonth = parseInt(monthStr, 10);
    const inputYear = parseInt(yearStr, 10);
    const now = new Date();
    return inputYear > now.getFullYear() || (inputYear === now.getFullYear() && inputMonth > now.getMonth() + 1);
  };

  const irAPeriodo = (delta) => {
    const candidato = shiftPeriodo(periodo, delta);
    if (delta > 0 && esPeriodoFuturo(candidato)) return;
    setPeriodo(candidato);
  };

  const simular = async () => {
    setErrorMsg("");

    if (esPeriodoFuturo(periodo)) {
      setErrorMsg("Ese periodo aún no está habilitado — es un mes futuro.");
      setResultado(null);
      return;
    }

    try {
      setLoading(true);
      const res = await axiosInstance.post(APIURL.postReporteSimuladorCredito(periodo, porcentaje));
      const fila = (res.data && res.data.length > 0) ? res.data[0] : {};

      setResultado({
        periodo,
        porcentaje,
        metaMensual: Number(fila.MetaMensual) || 0,
        metaDiaria: Number(fila.MetaDiaria) || 0,
        venta: Number(fila.Venta) || 0,
        ventaAcumulada: Number(fila.VentaAcumulada) || 0,
        ventaHoy: Number(fila.VentaHoy) || 0,
        faltante: Number(fila.Faltante) || 0,
        comisionProyectada: Number(fila.ComisionProyectada) || 0,
        tasaComision: Number(fila.TasaComision) || 0,
        bonoComision: Number(fila.BonoComision) || 0,
      });
    } catch (error) {
      console.error("Error al simular proyección:", error);
      setErrorMsg("No se pudo calcular la proyección. Intenta de nuevo.");
      setResultado(null);
    } finally {
      setLoading(false);
    }
  };

  // Primer cálculo automático al entrar (mes actual, 100%) — así el vendedor
  // ve su situación real sin tener que saber que debe presionar un botón.
  // Luego, cada cambio de % o periodo recalcula solo, con debounce para no
  // disparar un EXEC por cada paso del slider.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(simular, primerCargaRef.current ? 0 : 300);
    primerCargaRef.current = false;
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodo, porcentaje]);

  const sinComisionDefinida = !!resultado && resultado.tasaComision === 0 && resultado.bonoComision === 0;
  const montoPorTasa = resultado ? resultado.venta * resultado.tasaComision : 0;
  const siguienteEsFuturo = esPeriodoFuturo(shiftPeriodo(periodo, 1));

  const pctReal = resultado && resultado.metaMensual > 0 ? (resultado.ventaAcumulada / resultado.metaMensual) * 100 : 0;
  const tier = resultado ? tierAvance(pctReal) : null;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
      <Layout />
      <div className="flex-1 p-4 md:p-6 md:pt-4">
        <div className="mx-auto max-w-4xl space-y-3">

          {/* ── Header + navegación de periodo ───────────────────── */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-morado px-5 py-3.5 text-white shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-white/15">
                <Calculator className="h-4.5 w-4.5" />
              </div>
              <div>
                <h1 className="text-base font-bold leading-tight">Mi Meta de Ventas</h1>
                <p className="text-[11px] text-blue-100/90">Tu avance y lo que ganarías al cumplir</p>
              </div>
            </div>

            <div className="flex items-center gap-0.5 rounded-xl bg-white/10 pl-1 pr-1">
              <button
                type="button"
                onClick={() => irAPeriodo(-1)}
                aria-label="Mes anterior"
                className="cursor-pointer rounded-lg p-2 transition-colors hover:bg-white/15"
              >
                <ChevronLeft size={15} />
              </button>

              <div className="flex flex-col items-center px-1.5">
                <span className="text-sm font-bold leading-tight">{nombrePeriodo(periodo)}</span>
                <label className="flex items-center gap-1">
                  <Calendar size={9} className="text-blue-200/70" />
                  <input
                    type="text"
                    value={periodo}
                    onChange={e => setPeriodo(e.target.value)}
                    placeholder="MM/YYYY"
                    aria-label="Periodo en formato mes/año"
                    className="w-14 bg-transparent text-center text-[10px] text-blue-200/80 focus:text-white focus:outline-none"
                  />
                </label>
              </div>

              <button
                type="button"
                onClick={() => irAPeriodo(1)}
                disabled={siguienteEsFuturo}
                aria-label="Mes siguiente"
                className="cursor-pointer rounded-lg p-2 transition-colors hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ChevronRight size={15} />
              </button>
            </div>
          </div>

          {errorMsg && (
            <div className="flex items-start gap-2 rounded-xl border border-red-100 bg-red-50 p-3 text-xs font-semibold text-red-700">
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
              <p>{errorMsg}</p>
            </div>
          )}

          {loading && !resultado && (
            <div className="flex items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white py-14 text-slate-500 shadow-sm">
              <Loader2 size={20} className="animate-spin text-morado" />
              <span className="text-sm font-medium">Calculando tu proyección...</span>
            </div>
          )}

          {resultado && (
            <div className={`overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-opacity ${loading ? 'opacity-60' : 'opacity-100'}`}>

              {/* ── Encabezado del simulador ─────────────────────── */}
              <div className="flex items-center justify-between gap-2 p-4 pb-0">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-morado/10">
                    <Target size={15} className="text-morado" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-800">Mi meta de ventas</p>
                    <p className="text-[11px] text-slate-500">Tu avance real y lo que ganarías si simulas otro cierre</p>
                  </div>
                </div>
                {loading && <Loader2 size={16} className="flex-shrink-0 animate-spin text-morado" />}
              </div>

              {/* ── Izquierda: lo que llevas (fijo) · Derecha: lo que ganarías (controlado por el slider) ── */}
              <div className="relative mt-1 grid divide-y divide-slate-100 border-t border-slate-100 sm:grid-cols-2 sm:divide-x sm:divide-y-0">

                <div
                  className="pointer-events-none absolute left-1/2 top-1/2 z-10 hidden -translate-x-1/2 -translate-y-1/2 sm:flex"
                  aria-hidden="true"
                >
                  <div className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white shadow-sm">
                    <ArrowRight size={13} className="text-slate-400" />
                  </div>
                </div>

                {/* Izquierda — avance real, no depende del slider */}
                <div className="p-4">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
                      <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg bg-morado/10">
                        <Gauge size={12} className="text-morado" />
                      </div>
                      Avance actual
                    </div>
                    <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${tier.bg} ${tier.text}`}>
                      {tier.label}
                    </span>
                  </div>

                  <div className="flex flex-col items-center">
                    <div className="relative h-[124px] w-[124px]">
                      <RadialProgress pct={pctReal} size={124} stroke={11} color={tier.color} />
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-2xl font-black tracking-tight" style={{ color: tier.color }}>
                          {Math.round(pctReal)}%
                        </span>
                        <span className="text-[9px] font-bold uppercase tracking-wide text-slate-400">Avance</span>
                      </div>
                    </div>
                    <p className="mt-2 text-center text-[11px] text-slate-500">
                      Llevas <span className="font-bold text-slate-700">{fmtMoney(resultado.ventaAcumulada)}</span> de tu meta mensual.
                    </p>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <div className="rounded-xl bg-slate-50 p-2.5">
                      <p className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                        <Target size={10} /> Meta mensual
                      </p>
                      <p className="mt-0.5 text-sm font-bold text-slate-800">{fmtMoney(resultado.metaMensual)}</p>
                    </div>
                    <div className="rounded-xl bg-slate-50 p-2.5">
                      <p className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                        <BarChart3 size={10} /> Acumulado
                      </p>
                      <p className="mt-0.5 text-sm font-bold text-slate-800">{fmtMoney(resultado.ventaAcumulada)}</p>
                    </div>
                    <div className="rounded-xl bg-slate-50 p-2.5">
                      <p className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                        <Clock size={10} /> Meta diaria
                      </p>
                      <p className="mt-0.5 text-sm font-bold text-slate-800">{fmtMoney(resultado.metaDiaria)}</p>
                    </div>
                    <div className="rounded-xl bg-slate-50 p-2.5">
                      <p className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                        <DollarSign size={10} /> Venta de hoy
                      </p>
                      <p className="mt-0.5 text-sm font-bold text-slate-800">{fmtMoney(resultado.ventaHoy)}</p>
                    </div>
                  </div>
                </div>

                {/* Derecha — el slider vive solo aquí, controlando solo lo que
                    afecta. La comisión ya no usa un anillo (desbalanceaba la
                    altura frente a la izquierda): ahora es una tarjeta
                    horizontal compacta, del mismo alto que una fila de tiles. */}
                <div className="p-4">
                  <div className="mb-3 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
                    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg bg-primaryPurple/10">
                      <Wallet size={12} className="text-primaryPurple" />
                    </div>
                    Ganarías al {resultado.porcentaje}%
                  </div>

                  <div className="mb-3 border-b border-slate-100 pb-2.5">
                    <SliderPorcentaje value={porcentaje} onChange={setPorcentaje} />
                  </div>

                  {sinComisionDefinida ? (
                    <div className="mb-3 flex items-center gap-3 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 p-3">
                      <AlertCircle className="h-8 w-8 flex-shrink-0 text-slate-400" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-slate-600">Sin comisión definida para {resultado.porcentaje}%</p>
                        <p className="mt-0.5 text-[11px] text-slate-500">Prueba otro % o consulta a tu supervisor.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-3 flex items-center gap-2.5 rounded-xl border border-violet-200 bg-violet-50 p-2.5">
                      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primaryPurple text-white">
                        <Wallet size={15} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[9px] font-bold uppercase tracking-wide text-primaryPurple/70">Comisión si llegas al {resultado.porcentaje}%</p>
                        <p className="truncate text-xl font-black leading-tight tracking-tight text-primaryPurple">
                          {fmtMoney(resultado.comisionProyectada)}
                        </p>
                        <p className="mt-0.5 text-[11px] font-semibold">
                          {resultado.faltante <= 0 ? (
                            <span className="text-success">¡Ya alcanzaste el {resultado.porcentaje}%!</span>
                          ) : (
                            <span className="text-slate-500">Faltan {fmtMoney(resultado.faltante)} en ventas</span>
                          )}
                        </p>
                      </div>
                    </div>
                  )}

                  {!sinComisionDefinida && (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <div className="rounded-xl bg-slate-50 p-2.5">
                        <p className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                          <TrendingUp size={10} /> Venta proyectada
                        </p>
                        <p className="mt-0.5 text-sm font-bold text-slate-800">{fmtMoney(resultado.venta)}</p>
                      </div>
                      <div className="rounded-xl bg-slate-50 p-2.5">
                        <p className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                          {resultado.faltante <= 0 ? <Trophy size={10} /> : <Flag size={10} />} {resultado.faltante <= 0 ? 'Meta lograda' : 'Te falta'}
                        </p>
                        <p className={`mt-0.5 text-sm font-bold ${resultado.faltante <= 0 ? 'text-success' : 'text-slate-800'}`}>
                          {resultado.faltante <= 0 ? '¡Lograda!' : fmtMoney(resultado.faltante)}
                        </p>
                      </div>
                      <div className="rounded-xl bg-slate-50 p-2.5">
                        <p className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                          <Percent size={10} /> Por venta ({(resultado.tasaComision * 100).toFixed(2)}%)
                        </p>
                        <p className="mt-0.5 text-sm font-bold text-slate-800">{fmtMoney(montoPorTasa)}</p>
                      </div>
                      <div className="rounded-xl bg-slate-50 p-2.5">
                        <p className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                          <Gift size={10} /> Bono fijo
                        </p>
                        <p className="mt-0.5 text-sm font-bold text-slate-800">{fmtMoney(resultado.bonoComision)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {resultado && (
            <p className="flex items-start gap-1.5 px-1 text-[10px] text-slate-400">
              <Percent size={11} className="mt-0.5 flex-shrink-0" />
              La comisión se calcula con la tabla real de porcentajes por cargo, según tu % de cumplimiento y tu % de crédito.
            </p>
          )}

        </div>
      </div>
    </div>
  );
}
